import React, { useContext, useEffect, useRef, useState } from 'react';
import Modal from 'utils/components/modal';
import AssignUser from 'app/scripts/components/common/inputs/user-assignment';
import Teammates from 'app/scripts/components/common/users';
import { useDispatch } from 'react-redux';
import { ConfirmationModal } from '../../../components/moderation-v2/sharable/components/modals/ConfirmationModal';
import Switch from '../../common/inputs/customs/switch';
import setTwoWayVerification from '../../account/verification/actions/set-two-way-verification';
import { getUsersOfType } from '../../moderation-v2/users/state/reducer';
import { TModerationUser } from '../../moderation-v2/users/state/types';
import fetchTeamMembers from '../state/actions/fetch-team-users';
import { assignRole, Payload as RolePayload, unassignRole } from '../state/actions/roles';
import { Button } from '../../moderation-v2/sharable/components/Button/Button';
import FluxibleContext from '../../fluxible-context';
import { useTypedSelector } from 'state/store-types';
import { useSendEvent } from 'app/scripts/tracking/useSendEvent';
import logoutAccount from 'app/scripts/actions/account/logout';
import { createTwoFactorAuthTwoWayVerificationPayload } from '../../moderation/preferences/settings-send-event-utils';
import { CurrentUser } from '../../../../current-user/currentUserTypes';
import { Spot } from '../../../../spots/spotsTypes';

interface TeamMembersProps {
  spot: Spot;
  currentUser: CurrentUser;
}

type SearchType = 'username' | 'email';

export const TeamMembers = ({ spot, currentUser }: TeamMembersProps) => {
  const [searchUserBy, setSearchUserBy] = useState('username');
  const [placeholder, setPlaceholder] = useState('Type username...');
  const enableConfirmModalRef = useRef(null);
  const disableConfirmModalRef = useRef(null);
  const { executeAction } = useContext(FluxibleContext);
  const { users } = useTypedSelector(state => getUsersOfType(state, TModerationUser.TeamMembers));
  const { sendEvent } = useSendEvent();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchTeamMembers());
  }, [dispatch]);

  const openEnableConfirmModal = () => {
    enableConfirmModalRef.current.show();
  };

  const closeEnableConfirmModal = () => {
    enableConfirmModalRef.current.hide();
  };

  const openDisableConfirmModal = () => {
    disableConfirmModalRef.current.show();
  };

  const closeDisableConfirmModal = () => {
    disableConfirmModalRef.current.hide();
  };

  const handleTwoStepVerification = (value: boolean): void => {
    if (value) {
      openEnableConfirmModal();
    } else {
      openDisableConfirmModal();
    }
  };

  const sendTwoWayVerificationEvent = (status: boolean, be_success: boolean) => {
    sendEvent(createTwoFactorAuthTwoWayVerificationPayload(status, be_success));
  };

  const twoWayVerification = () => {
    const status = !spot.isRequiredTwoStepVerification;
    const spotId = spot.id;
    executeAction(setTwoWayVerification, {
      spotId,
      status,
      sendTwoWayVerificationEvent,
      successCb: () => {
        executeAction(logoutAccount, { spotId });
      }
    });
    if (status) {
      closeEnableConfirmModal();
    } else {
      closeDisableConfirmModal();
    }
  };

  const onSearchTypeChange = (type: SearchType): void => {
    let placeholder = 'Type username...';
    if (type === 'email') {
      placeholder = 'Enter user email (exact match)';
    }
    setSearchUserBy(type);
    setPlaceholder(placeholder);
  };

  return (
    <div className="preferences-premissions">
      <AssignUser
        title="Teammates"
        description="Assign role players to help manage your community."
        teammates={users}
        spotId={spot.id}
        onSearchTypeChange={onSearchTypeChange}
        searchUserBy={searchUserBy}
        placeholder={placeholder}
        type="teammates"
        hasExport
      />
      <Teammates
        spot={spot}
        users={users}
        currentUserId={currentUser.id}
        type="teammates"
        addUser={(props: RolePayload) => dispatch(assignRole(props))}
        removeUser={(props: RolePayload) => dispatch(unassignRole(props))}
      />
      <Switch
        title="Enable 2-step Verification"
        description="All admins and moderators will be required to enter a verification code received via text message in order to access your account."
        value={spot.isRequiredTwoStepVerification}
        onChange={handleTwoStepVerification}
      />

      <Modal ref={enableConfirmModalRef} className="confirm-modal">
        <ConfirmationModal
          onClose={closeEnableConfirmModal}
          title="Enable Two-Factor Authentication?"
          titleClassName="title"
          description="This will increase your security but will also require you to enter a verification code in addition to your password whenever you log in."
          renderActions={() => (
            <div className="modal-action-buttons">
              <Button theme="contained-apply" onClick={twoWayVerification}>
                Enable
              </Button>
              <Button theme="outlined-discard" onClick={closeEnableConfirmModal}>
                Cancel
              </Button>
            </div>
          )}
        />
      </Modal>

      <Modal ref={disableConfirmModalRef} className="confirm-modal">
        <ConfirmationModal
          onClose={closeDisableConfirmModal}
          title="Disable Two-Factor Authentication?"
          titleClassName="title"
          description="This will make your account easier to access but less secure. You will no longer need to enter a verification code in addition to your password when you log in."
          renderActions={() => (
            <div className="modal-action-buttons">
              <Button theme="outlined-apply-dangerous" onClick={twoWayVerification}>
                Disable
              </Button>
              <Button theme="outlined-discard" onClick={closeDisableConfirmModal}>
                Cancel
              </Button>
            </div>
          )}
        />
      </Modal>
    </div>
  );
};
